import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import {
  FieldType,
  FormlyFieldConfig,
  FormlyFormOptions,
} from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, merge, Subject } from 'rxjs';
import { JourneyMappingService } from '../../../services/journey-mapping.service';
import {
  filter,
  debounceTime,
  map,
  concatMap,
  catchError,
  distinctUntilChanged,
  take,
} from 'rxjs/operators';
import { IEnvironment } from '../../../models/environment.model';
import { IService } from '../../../models/services-list.model';
import { IServiceTranslation } from '../../../models/service-translation.model';
import { ToastService } from '../../../services/toast/toast.service';
import { FormStateService } from '../../../services/formly/form-state.service';
import { PublicServicesService } from '../../../services/public-services/public-services.service';
import {
  performFormTranslations,
  resetFormTranslations,
  TranslationContext,
} from '../../../../utils/utils/form-translations.util';
import { findFormControlInScope } from '../../../../utils/utils/data-fetch-widget-utils';
import { IFeedbackResponseDetails } from '../../../models/application-details.model';
import { EIremboFormlyFormStateKeys } from '../../../models/irembo-formly-formstate-keys.enum';

/**
 * Interface for field mapping configuration
 */
interface IFieldMapping {
  keyValue?: string;
  valueKey?: string;
  targetKey: string;
  isStatic?: boolean;
  staticValue?: any;
  additionalFieldProps?: Record<string, any>;
}

/**
 * Enum for tracking model initialization sources
 */
enum ModelInitializationSource {
  DEFAULT = 'default',
  DRAFT = 'draft',
  PREFILL = 'prefill',
}

/**
 * Renders a single service form, handling its data loading, translations,
 * and optional toggling.
 */
@Component({
  selector: 'irembogov-single-service-render',
  templateUrl: './single-service-render.component.html',
  styleUrls: ['./single-service-render.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SingleServiceRenderComponent
  extends FieldType<FormlyFieldConfig>
  implements OnInit, OnDestroy
{
  // Service identifiers and properties
  serviceId?: string;
  changeRequestId?: string;
  serviceCode = '';

  // UI state
  loadingState = false;
  errorState = false;
  errorMessage = '';

  // Debug mode
  private debugMode = false;

  private readonly environment: IEnvironment;

  // Internal Formly form properties
  serviceModel: Record<string, unknown> = {}; // Model for the nested formly-form
  serviceForm = new FormGroup({}); // FormGroup for the nested formly-form
  serviceOptions: FormlyFormOptions = {};

  // Layout and translations
  serviceLayout: FormlyFieldConfig[] | undefined;
  translatedServiceLayout: FormlyFieldConfig[] = [];
  serviceTranslations?: IServiceTranslation;
  journeyFeedbacksForService: IFeedbackResponseDetails[] = [];

  private serviceLoaded = false; // Tracks if the service (layout, etc.) has been fetched
  private modelInitialized = false; // Tracks if the service model has been initialized with real data

  /**
   * Tracks if field mappings have been applied to prevent duplicate applications.
   *
   * Lifecycle:
   * - Initialized as `false`
   * - Set to `true` after first successful mapping application (in applyFieldMappings)
   * - Reset to `false` when field becomes visible after being hidden (in handleFieldVisibilityChange)
   *
   * This ensures mappings apply exactly once per visibility cycle, preventing
   * user-entered data from being overwritten by re-triggered mappings.
   */
  private mappingsApplied = false;
  private readonly subscriptions = new Subscription();

  // Enhanced state management
  private readonly changeDetectionSubject = new Subject<void>();
  private readonly mappingQueue = new Subject<IFieldMapping[]>();

  constructor(
    private readonly publicServicesService: PublicServicesService,
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService,
    private readonly formStateService: FormStateService,
    private readonly journeyMappingService: JourneyMappingService,
    private readonly cd: ChangeDetectorRef,
    @Inject('environment') environment: IEnvironment
  ) {
    super();
    this.environment = environment;
  }

  ngOnInit(): void {
    this.validateConfiguration();

    // Initialize debug mode from props
    this.debugMode = this.props?.['debugMode'] === true;

    if (!this.serviceCode) {
      this.handleError(
        'Service code is required for single-service-render component'
      );
      return;
    }

    this.initializeComponentState();
    this.initializeFormStateOptions();
    this.initializeSubscriptions();
    this.handleFieldVisibilityChange();
    this.setupChangeDetectionOptimization();
    this.setupMappingQueue();
  }

  /**
   * Initialize form state options once during component setup
   */
  private initializeFormStateOptions(): void {
    const currentFormState =
      this.formStateService.getFormlyFormStateSubject().value;

    this.serviceOptions = {
      ...this.serviceOptions,
      formState: {
        ...(this.serviceOptions.formState ?? {}),
        ...currentFormState,
        getTranslation: this.formStateService.getTranslation.bind(
          this.formStateService
        ),
        translate: this.formStateService.translate,
      },
    };

    this.updateJourneyFeedbacks(currentFormState);
  }

  /**
   * Setup optimized change detection with debouncing
   */
  private setupChangeDetectionOptimization(): void {
    this.subscriptions.add(
      this.changeDetectionSubject
        .pipe(debounceTime(50)) // Debounce rapid changes
        .subscribe(() => {
          this.cd.markForCheck();
        })
    );
  }

  /**
   * Setup mapping queue with concurrency control
   */
  private setupMappingQueue(): void {
    this.subscriptions.add(
      this.mappingQueue
        .pipe(
          debounceTime(100), // Debounce rapid mapping requests
          concatMap(mappings => this.processFieldMappings(mappings)),
          catchError(error => {
            console.error('Failed to process field mappings', error);
            return [];
          })
        )
        .subscribe()
    );
  }

  /**
   * Schedule optimized change detection
   */
  private scheduleChangeDetection(): void {
    this.changeDetectionSubject.next();
  }

  /**
   * Unified subscription management system
   */
  private initializeSubscriptions(): void {
    this.setupFormSubscriptions();
    this.setupExternalSubscriptions();
  }

  /**
   * Setup form-related subscriptions
   */
  private setupFormSubscriptions(): void {
    // Internal service form changes
    const serviceFormChanges$ = this.serviceForm.valueChanges.pipe(
      filter(() => !this.field.hide),
      map(value => ({ type: 'SERVICE_FORM_CHANGE' as const, payload: value }))
    );

    // Parent form changes for dynamic updates and draft detection
    // Only subscribe if there are field mappings configured
    const parentFormChanges$ = this.form.valueChanges.pipe(
      filter(() => {
        const hasFieldMappings = (this.props['fieldMapping'] || []).length > 0;
        return this.serviceLoaded && !this.applyingMappings && hasFieldMappings;
      }),
      map(value => ({ type: 'FORM_CHANGE' as const, payload: value }))
    );

    // Root form changes for draft data initialization
    const rootFormChanges$ = this.form.root.valueChanges.pipe(
      map(value => ({ type: 'ROOT_FORM_CHANGE' as const, payload: value }))
    );

    // Combine form-related streams
    const formEvents$ = merge(
      serviceFormChanges$,
      parentFormChanges$,
      rootFormChanges$
    );

    this.subscriptions.add(
      formEvents$.subscribe(event => this.handleFormEvents(event))
    );
  }

  /**
   * Setup external subscriptions (mappings, translations, form state)
   */
  private setupExternalSubscriptions(): void {
    // Field mapping events - only if field mappings are configured
    const mappingEvents$ = this.journeyMappingService.fieldMapping$.pipe(
      filter(mapping => {
        const hasFieldMappings = (this.props['fieldMapping'] || []).length > 0;
        return mapping !== null && hasFieldMappings;
      }),
      map(mapping => ({ type: 'MAPPING' as const, payload: mapping }))
    );

    // Language change events
    const langEvents$ = this.translateService.onLangChange.pipe(
      map(event => ({ type: 'LANG_CHANGE' as const, payload: event }))
    );

    // Form state events - use distinctUntilChanged like parent component
    // This prevents redundant updates that interfere with expression evaluation
    const stateEvents$ = this.formStateService.getFormlyFormStateSubject().pipe(
      // Use distinctUntilChanged to avoid processing same formState multiple times
      // Without it, every emission triggers expression re-evaluation
      distinctUntilChanged((prev, curr) => {
        // Deep equality check on relevant formState properties
        return JSON.stringify(prev) === JSON.stringify(curr);
      }),
      map(formState => ({
        type: 'STATE_CHANGE' as const,
        payload: formState,
      }))
    );

    // Combine external streams
    const externalEvents$ = merge(mappingEvents$, langEvents$, stateEvents$);

    this.subscriptions.add(
      externalEvents$.subscribe(event => this.handleExternalEvents(event))
    );
  }

  /**
   * Handle form-related events
   */
  private handleFormEvents(event: { type: string; payload: any }): void {
    try {
      switch (event.type) {
        case 'SERVICE_FORM_CHANGE':
          this.onServiceFormChange();
          break;
        case 'FORM_CHANGE':
          this.queueFieldMappings();
          break;
        case 'ROOT_FORM_CHANGE':
          this.handleParentModelChange(event.payload);
          break;
      }
    } catch (error) {
      console.error(`Failed to handle form event: ${event.type}`, error);
    }
  }

  /**
   * Handle external events
   */
  private handleExternalEvents(event: { type: string; payload: any }): void {
    try {
      switch (event.type) {
        case 'MAPPING':
          this.queueFieldMappings();
          break;
        case 'LANG_CHANGE':
          if (this.serviceLoaded) {
            this.fetchServiceTranslations(event.payload.lang);
          }
          break;
        case 'STATE_CHANGE':
          this.updateFormStateOptions(event.payload);
          break;
      }
    } catch (error) {
      console.error(`Failed to handle external event: ${event.type}`, error);
    }
  }

  /**
   * Queue field mappings for processing
   */
  private queueFieldMappings(): void {
    const fieldMapping = this.props['fieldMapping'] || [];

    // Early return if no mappings configured
    if (fieldMapping.length === 0) {
      return;
    }

    this.mappingQueue.next(fieldMapping);
  }

  /**
   * Update form state options when formState changes
   * Creating new object references causes Formly to re-evaluate ALL expressions
   */
  private updateFormStateOptions(formState: any): void {
    // Ensure serviceOptions.formState exists
    if (!this.serviceOptions.formState) {
      this.serviceOptions.formState = {};
    }

    // Mutate the formState object instead of creating new reference
    // This prevents Formly from detecting a change in serviceOptions
    Object.keys(formState).forEach(key => {
      this.serviceOptions.formState![key] = formState[key];
    });

    // Update journey feedbacks
    this.updateJourneyFeedbacks(formState);

    // Trigger change detection
    this.scheduleChangeDetection();
  }

  private updateJourneyFeedbacks(formState: any): void {
    const feedbacks = formState?.[EIremboFormlyFormStateKeys.JOURNEY_FEEDBACKS];

    if (Array.isArray(feedbacks)) {
      this.journeyFeedbacksForService = feedbacks.filter(feedback => {
        if (!feedback) {
          return false;
        }

        const matchesService =
          feedback.serviceCode && feedback.serviceCode === this.serviceCode;

        return (
          matchesService &&
          feedback.activeFeedback === true &&
          typeof feedback.comment === 'string' &&
          feedback.comment.trim().length > 0
        );
      });
    } else {
      this.journeyFeedbacksForService = [];
    }
  }

  trackJourneyFeedback(_: number, feedback: IFeedbackResponseDetails): string {
    const identifier = feedback.applicationNumber ?? feedback.serviceCode ?? '';
    return `${identifier}-${feedback.comment ?? ''}`;
  }

  /**
   * Process field mappings with proper error handling
   */
  private processFieldMappings(_mappings: IFieldMapping[]): Promise<void> {
    return new Promise(resolve => {
      try {
        this.applyFieldMappings();
        resolve();
      } catch (error) {
        console.error('Error processing field mappings', error);
        resolve();
      }
    });
  }

  /**
   * Fetches the Formly field configurations for the service.
   */
  private fetchServiceFormLayout(): void {
    if (!this.changeRequestId) {
      this.handleError('Change Request ID is missing, cannot load form.');
      return;
    }

    this.subscriptions.add(
      this.publicServicesService
        .getChangeRequestApplicationForm(this.changeRequestId)
        .subscribe({
          next: (formLayout: FormlyFieldConfig[]) => {
            this.serviceLayout = formLayout;
            this.translatedServiceLayout = JSON.parse(
              JSON.stringify(formLayout)
            ); // Deep clone for translations

            // Only set defaults if not already initialized with real data
            if (
              !this.modelInitialized &&
              Object.keys(this.serviceModel).length === 0
            ) {
              this.serviceModel = this.getDefaultModel();
            }

            this._updateParentFormFieldValue(); // Update parent form
            this.applyTranslations(this.currentLocale);

            // Service is now loaded - apply any mappings
            this.serviceLoaded = true;
            this.queueFieldMappings();

            this.loadingState = false;
            this.scheduleChangeDetection();
          },
          error: err => this.handleError('Failed to load service form', err),
        })
    );
  }

  private applyingMappings = false;

  /**
   * Apply all field mappings for this service
   * Uses the same pattern as populateFormFields utility for control updates
   */
  private applyFieldMappings(): void {
    const parentModel = this.form?.root?.value || this.model;

    // Check if draft data just arrived and we haven't initialized yet
    if (!this.modelInitialized && this.serviceLoaded) {
      const serviceDataInParent = parentModel[this.serviceCode];
      if (serviceDataInParent && this.hasRealServiceData(serviceDataInParent)) {
        this.initializeWithDraftData(serviceDataInParent);
        return; // Exit early, field mappings will be applied in the next cycle
      }
    }

    // Only apply mappings once after initial form load
    // This prevents overwriting user-entered data on subsequent parent form changes
    if (this.mappingsApplied) {
      return;
    }

    if (!this.serviceLayout || this.applyingMappings) {
      return;
    }

    const fieldMapping = this.props['fieldMapping'] || [];

    if (fieldMapping.length === 0) {
      return;
    }

    this.debugLog('applyFieldMappings', 'Applying mappings (first time only)', {
      mappingCount: fieldMapping.length,
    });

    this.applyingMappings = true;

    try {
      // Process each field mapping
      for (const mapping of fieldMapping) {
        const keyPath = mapping.keyValue || mapping.valueKey;
        const targetKey = mapping.targetKey;

        // Get source value from parent model (field.model, not form.root.value) or static value
        // Using field.model ensures we always have the latest data
        const sourceValue = mapping.isStatic
          ? mapping.staticValue
          : this.getNestedValue(parentModel, keyPath);

        // Find the target field configuration
        const targetFieldConfig = this.findFieldInFormLayout(
          this.translatedServiceLayout,
          targetKey
        );

        // Apply value to form control - Formly will update this.serviceModel automatically
        // Check for null/undefined specifically to allow 0, false, and empty strings
        if (sourceValue !== null && sourceValue !== undefined) {
          const formControl = findFormControlInScope(
            this.serviceForm,
            targetKey
          );

          if (formControl) {
            this.debugLog(
              'applyFieldMappings',
              `Applying mapping to ${targetKey}`,
              {
                targetKey,
                sourceValue,
                isFormArray: formControl instanceof FormArray,
              }
            );

            // Initialize FormArray structure if needed (follows pattern from data-fetch-widget-utils.ts)
            if (
              formControl instanceof FormArray &&
              Array.isArray(sourceValue)
            ) {
              this.formStateService.intializeRepeater({
                items: sourceValue,
                key: targetKey.split('.').pop() ?? '',
              });
            }

            // Reset and patch value (for both FormArray and regular controls)
            formControl.reset();
            formControl.patchValue(sourceValue ?? null);

            // Additional updates for non-FormArray controls
            if (!(formControl instanceof FormArray)) {
              formControl.markAsDirty();
              formControl.markAsTouched();
              formControl.updateValueAndValidity();

              // Trigger change detection on the specific field
              // Critical for OnPush components like TIN input
              if (targetFieldConfig) {
                targetFieldConfig.options?.build?.(targetFieldConfig);
                targetFieldConfig.options?.detectChanges?.(targetFieldConfig);
              }
            }

            this.debugLog(
              'applyFieldMappings',
              `Value applied to ${targetKey}`,
              {
                targetKey,
                appliedValue: formControl.value,
                isFormArray: formControl instanceof FormArray,
              }
            );
          }
        }

        // Apply field properties (readonly, required, etc.)
        if (mapping.additionalFieldProps) {
          const fieldConfig = this.findFieldInFormLayout(
            this.translatedServiceLayout,
            targetKey
          );
          if (fieldConfig) {
            fieldConfig.props = fieldConfig.props || {};
            Object.assign(fieldConfig.props, mapping.additionalFieldProps);

            // Handle validation changes
            if ('required' in mapping.additionalFieldProps) {
              if (mapping.additionalFieldProps.required === false) {
                fieldConfig.validators = undefined;
                fieldConfig.validation = undefined;
              } else if (mapping.additionalFieldProps.required === true) {
                fieldConfig.validators = { validation: ['required'] };
              }
            }

            // Handle hideField changes - ensure wrapper gets notified
            if ('hideField' in mapping.additionalFieldProps) {
              if (mapping.additionalFieldProps.hideField) {
                // Add hidden class
                const classes = fieldConfig.className
                  ? fieldConfig.className.split(' ')
                  : [];
                if (!classes.includes('irembo-formly-hidden-field')) {
                  classes.push('irembo-formly-hidden-field');
                }
                fieldConfig.className = classes.join(' ');
              } else {
                // Remove hidden class
                if (fieldConfig.className) {
                  const classes = fieldConfig.className
                    .split(' ')
                    .filter(c => c !== 'irembo-formly-hidden-field');
                  fieldConfig.className = classes.join(' ');
                }
              }

              // Trigger field change detection to notify wrapper
              fieldConfig.options?.detectChanges?.(fieldConfig);
            }
          }
        }
      }

      // Update parent form field value
      this._updateParentFormFieldValue();

      // Trigger change detection to show updated values
      this.cd.detectChanges();

      // Mark mappings as applied - won't re-apply on subsequent parent form changes
      this.mappingsApplied = true;

      this.debugLog('applyFieldMappings', 'Mappings applied successfully', {
        mappingsApplied: this.mappingsApplied,
      });
    } finally {
      this.applyingMappings = false;
    }
  }

  private isObject(item: any): boolean {
    return item && typeof item === 'object' && !Array.isArray(item);
  }

  private getNestedValue(obj: any, path: string): any {
    if (!path || !obj) {
      return undefined;
    }

    // Handle paths that start with "model." - remove the "model." prefix
    const cleanPath = path.startsWith('model.') ? path.substring(6) : path;

    // Special handling for cross-service references
    // If path starts with a service code that's not the current service, look in the parent model
    const pathParts = cleanPath.split('.');
    const firstPart = pathParts[0];

    // Check if the first part looks like a service code (starts with SRV)
    if (firstPart.startsWith('SRV') && firstPart !== this.serviceCode) {
      // This is a cross-service reference, look in the parent model

      // Use parent model for cross-service lookups and use the original path (without removing service code)
      if (this.field?.parent?.model) {
        // Directly traverse the path in the parent model without recursive call to avoid infinite loop
        const result = cleanPath.split('.').reduce((current, key) => {
          return current && current[key] !== undefined
            ? current[key]
            : undefined;
        }, this.field.parent.model);

        return result;
      }
    }

    const result = cleanPath.split('.').reduce((current, key) => {
      const nextValue =
        current && current[key] !== undefined ? current[key] : undefined;
      return nextValue;
    }, obj);

    return result;
  }

  private updateFormControlsRecursively(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control) {
        control.updateValueAndValidity({ onlySelf: false, emitEvent: false });
        if (control instanceof FormGroup) {
          this.updateFormControlsRecursively(control);
        }
      }
    });
  }

  private findFieldInFormLayout(
    layout: FormlyFieldConfig[],
    fieldKey: string
  ): FormlyFieldConfig | undefined {
    const searchField = (
      fields: FormlyFieldConfig[]
    ): FormlyFieldConfig | undefined => {
      for (const field of fields) {
        if (field.key === fieldKey) {
          return field;
        }
        if (field.fieldGroup && field.fieldGroup.length > 0) {
          const found = searchField(field.fieldGroup);
          if (found) {
            return found;
          }
        }
      }
      return undefined;
    };

    return searchField(layout);
  }

  private validateConfiguration(): void {
    this.serviceCode = this.props?.['code'] ?? '';
  }

  /**
   * Get service data from parent model (standard Formly pattern)
   */
  private get serviceData(): Record<string, unknown> {
    return this.field.model?.[this.serviceCode] || {};
  }

  /**
   * Check if service data contains actual form data (not just metadata)
   */
  private hasRealServiceData(data: any): boolean {
    if (!data || typeof data !== 'object') {
      return false;
    }
    // Check for keys other than metadata
    const realKeys = Object.keys(data).filter(key => key !== '__formLayout');
    if (realKeys.length === 0) {
      return false;
    }

    for (const key of realKeys) {
      const value = data[key];
      if (
        value != null &&
        (typeof value !== 'object' || Object.keys(value).length > 0)
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get default model for the service form
   */
  private getDefaultModel(): Record<string, unknown> {
    if (this.serviceLayout) {
      return this._getDefaultNestedFormModel(this.serviceLayout);
    }
    return {};
  }

  /**
   * Initialize service model using standard Formly field pattern
   */
  private initializeComponentState(): void {
    this.initializeServiceModel(ModelInitializationSource.DEFAULT);

    if (!this.field.hide) {
      this.loadServiceData();
    }
  }

  /**
   * Unified service model initialization with source tracking
   */
  private initializeServiceModel(
    source: ModelInitializationSource,
    data?: any
  ): void {
    try {
      switch (source) {
        case ModelInitializationSource.DRAFT:
          this.initializeFromDraft(data);
          break;
        case ModelInitializationSource.PREFILL:
          this.initializeFromPrefill(data);
          break;
        case ModelInitializationSource.DEFAULT:
        default:
          this.initializeFromDefaults();
          break;
      }

      // Update parent form with the initialized model
      this._updateParentFormFieldValue();
      this.scheduleChangeDetection();
    } catch (error) {
      console.error(`Failed to initialize service model from ${source}`, error);
    }
  }

  /**
   * Initialize from existing draft data
   */
  private initializeFromDraft(draftData?: any): void {
    const dataToUse = draftData || this.serviceData;

    if (dataToUse && this.hasRealServiceData(dataToUse)) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { __formLayout: _, ...actualServiceData } = dataToUse as any;
      this.serviceModel = {
        ...actualServiceData,
        ...this.props['serviceFormProps'],
      };
      this.modelInitialized = true;
    } else {
      this.initializeFromDefaults();
    }
  }

  /**
   * Initialize from prefill data
   */
  private initializeFromPrefill(prefillData: any): void {
    if (prefillData && typeof prefillData === 'object') {
      this.serviceModel = {
        ...this.serviceModel,
        ...prefillData,
        ...this.props['serviceFormProps'],
      };
      this.modelInitialized = true;
    } else {
      this.initializeFromDefaults();
    }
  }

  /**
   * Initialize with default values
   */
  private initializeFromDefaults(): void {
    const existingData = this.serviceData;

    if (existingData && this.hasRealServiceData(existingData)) {
      this.initializeFromDraft(existingData);
    } else {
      this.serviceModel = {
        ...this.props['serviceFormProps'],
      };
      this.modelInitialized = false;
    }
  }

  private loadServiceData(): void {
    if (this.loadingState) return;

    this.loadingState = true;
    this.errorState = false;
    this.scheduleChangeDetection();

    this.subscriptions.add(
      this.publicServicesService.getServiceByCode(this.serviceCode).subscribe({
        next: (service: IService) => {
          this.serviceId = service.id;
          this.changeRequestId = service.changeRequestId;
          this.formStateService.setServiceContext({
            service: service.serviceName,
            serviceId: service.id,
            serviceCode: this.serviceCode,
            institutionId: service.organization?.id,
            institutionName: service.organization?.name,
            institutionAcronym: service.organization?.acronym,
          });
          this.fetchServiceFormLayout();
          this.fetchServiceTranslations(this.currentLocale);
        },
        error: err => {
          this.handleError('Failed to load service details', err);
          this.serviceLoaded = false;
        },
      })
    );
  }

  private handleFieldVisibilityChange(): void {
    if (this.field) {
      let currentHideValue = this.field.hide;
      this.debugLog(
        'handleFieldVisibilityChange',
        'Setting up visibility change handler',
        {
          initialHideValue: currentHideValue,
        }
      );

      Object.defineProperty(this.field, 'hide', {
        get: () => currentHideValue,
        set: (newValue: boolean) => {
          const wasHidden = currentHideValue;
          currentHideValue = newValue;

          this.debugLog('handleFieldVisibilityChange', 'Visibility changed', {
            wasHidden,
            newValue,
            changed: wasHidden !== newValue,
            currentModel: { ...this.serviceModel },
            formControlValue: this.formControl?.value,
          });

          if (wasHidden !== newValue) {
            if (newValue) {
              this.debugLog(
                'handleFieldVisibilityChange',
                'Field hidden - CLEARING FORM',
                {
                  modelBeforeClear: { ...this.serviceModel },
                  parentModelBefore: this.field.parent?.model,
                }
              );

              // Clear the form control - Formly will automatically update this.serviceModel
              // Following same pattern as applyFieldMappings
              this.formControl.patchValue({});
              this.formControl.markAsDirty();
              this.formControl.updateValueAndValidity();

              // Update parent form field value
              this._updateParentFormFieldValue();

              // Trigger change detection
              this.cd.detectChanges();

              this.debugLog('handleFieldVisibilityChange', 'Form cleared', {
                modelAfterClear: this.serviceModel,
                formControlValue: this.formControl?.value,
              });
            } else {
              this.debugLog(
                'handleFieldVisibilityChange',
                'Field shown - REINITIALIZING',
                {
                  modelBeforeReinit: { ...this.serviceModel },
                }
              );

              // Reset mappings flag so they can be re-applied when field becomes visible
              this.mappingsApplied = false;
              this.initializeComponentState();

              this.debugLog(
                'handleFieldVisibilityChange',
                'Field reinitialized',
                {
                  modelAfterReinit: this.serviceModel,
                }
              );
            }
          }
        },
        configurable: true,
      });
    }
  }

  /**
   * Update parent model using standard pattern
   */
  private _updateParentFormFieldValue(): void {
    if (!this.formControl || !this.field) {
      return;
    }

    const newServiceData = {
      ...this.serviceModel,
      __formLayout: this.serviceLayout, // Keep the layout for metadata used in translations and application summary
    };

    if (!this.field.model) {
      (this.field as any).model = {};
    }
    (this.field as any).model[this.serviceCode] = newServiceData;

    // Update form control value to match the model
    // The formControl.value should BE the service data directly
    // Using emitEvent: true to allow parent expressions to re-evaluate
    // The modelInitialized guard in handleParentModelChange prevents infinite loops
    this.formControl.patchValue(newServiceData, { emitEvent: true });
    this.formControl.markAsDirty();

    this.debugLog('_updateParentFormFieldValue', 'Child synced to parent', {
      fieldCount: Object.keys(this.serviceModel).length,
    });
  }

  private get currentLocale(): string {
    return localStorage.getItem('locale') ?? 'en-US';
  }

  private fetchServiceTranslations(locale: string): void {
    if (!this.serviceId) return;

    this.subscriptions.add(
      this.publicServicesService
        .getServiceTranslationsByLocale(
          this.serviceId,
          locale,
          this.environment.apiGatewayBaseUrl + '/service'
        )
        .subscribe({
          next: responses => {
            this.serviceTranslations = responses.data;
            this.applyTranslations(locale);
          },
          error: () => {
            this.serviceTranslations = undefined;
            this.applyTranslations(locale);
          },
        })
    );
  }

  private applyTranslations(locale: string): void {
    if (!this.serviceLayout) return;

    if (this.serviceTranslations && this.translatedServiceLayout?.length) {
      const translationContext: TranslationContext = {
        translatedLayout: this.translatedServiceLayout,
        layout: this.serviceLayout,
        serviceTranslations: this.serviceTranslations,
        translateService: this.translateService,
      };
      this.translatedServiceLayout = performFormTranslations(
        translationContext,
        locale
      );
    } else {
      this.translatedServiceLayout = resetFormTranslations(this.serviceLayout);
    }
    this.scheduleChangeDetection();
  }

  /**
   * Handle service form changes using standard pattern
   */
  private onServiceFormChange(): void {
    this._updateParentFormFieldValue();
  }

  /**
   * Handle parent model changes - initialize service model with draft data if available
   */
  private handleParentModelChange(parentValue: any): void {
    if (!parentValue || !this.serviceLoaded || this.modelInitialized) {
      return;
    }

    const newServiceData = parentValue[this.serviceCode];

    // Check if we have actual service data that just arrived (draft loading)
    if (newServiceData && this.hasRealServiceData(newServiceData)) {
      this.initializeWithDraftData(newServiceData);
    }
  }

  /**
   * Initialize service model with draft data - only happens once
   */
  private initializeWithDraftData(serviceSpecificData: any): void {
    if (this.modelInitialized) {
      return;
    }

    this.initializeServiceModel(
      ModelInitializationSource.DRAFT,
      serviceSpecificData
    );

    // Update the form with the new model
    this.serviceForm.patchValue(this.serviceModel, { emitEvent: false });

    // Apply field mappings after form stabilizes to ensure mapped fields
    // stay readonly and have correct values from mappings
    this.serviceForm.statusChanges.pipe(take(1)).subscribe(() => {
      this.queueFieldMappings();
    });
  }

  private _getDefaultNestedFormModel(
    form: FormlyFieldConfig[]
  ): Record<string, unknown> {
    const model: Record<string, unknown> = {};
    const formFields = form ?? [];
    for (const field of formFields) {
      if (field.key && typeof field.key === 'string') {
        if (field.fieldGroup && field.fieldGroup.length > 0) {
          model[field.key] = this._getDefaultNestedFormModel(field.fieldGroup);
        } else if (!field.fieldGroup) {
          model[field.key] = field.props?.['defaultValue'] ?? null;
        }
      } else if (field.fieldGroup && field.fieldGroup.length > 0) {
        Object.assign(model, this._getDefaultNestedFormModel(field.fieldGroup));
      }
    }
    return model;
  }

  private handleError(messageKey: string, error?: any): void {
    this.errorState = true;
    this.loadingState = false;

    const backendMessage = error?.error?.responseMessage ?? error?.message;
    const translatedMessage = this.translateService.instant(messageKey);
    this.errorMessage = backendMessage ?? translatedMessage;

    this.cd.markForCheck();
    this.toastService.show({
      body: this.errorMessage,
      type: 'error',
    });
  }

  /**
   * Debug logging utility - only logs when debugMode is enabled
   */
  private debugLog(method: string, message: string, data?: any): void {
    if (!this.debugMode) return;

    const logData = {
      timestamp: new Date().toISOString(),
      component: 'SingleServiceRenderComponent',
      serviceCode: this.serviceCode,
      method,
      message,
      ...(data && { data }),
    };

    console.log(`[customserviceviewer:${this.serviceCode}]`, logData);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.changeDetectionSubject.complete();
    this.mappingQueue.complete();
  }
}
